/**
 * «Планирование ресурсов»: хватит ли техники, воды и денег на площадь в рабочем
 * окне (решения №1871–1873).
 *
 * Что берётся из канона, а что задаёт человек:
 *  - площадь — земля агроцикла (`/agrocycles`, участники по размещению);
 *  - окно — окно выбранной работы плана агроцикла (`/agrocycles/{key}`), а если
 *    у работы дат нет — окно её этапа, затем окно плана;
 *  - вода — подано по файлу ирригации (`/water/district`);
 *  - техника, производительность, норма воды, затраты и бюджет — допущения
 *    пользователя: таких сущностей в базе района нет. Они хранятся только в
 *    браузере (`localStorage`) и подписаны «черновик».
 *
 * Никаких фикстур: нет агроциклов или работ плана — `EmptyState`, а не ошибка;
 * запрос упал — `LoadError` с «Повторить».
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowUpRight, CalendarClock, Droplets, RotateCcw, Tractor, Wallet } from 'lucide-react';
import { api } from '../../api/client';
import { useRequest } from '../../api/useRequest';
import { useAnalysisContext } from '../../analysis/context';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { srv } from '../../i18n/serverText';
import { localIsoDate } from '../../lib/localDate';
import { usePageTrail } from '../shell/ShellContext';
import { cycleLabel } from '../data-source/api/compare';
import { agrocycleApi, type AgrocycleDetail, type AgrocycleRow } from '../pages/agrocycle/agrocycleApi';
import { dateDay, haUnit, m3Compact, num, windowLabel } from '../lib/format';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { ChoiceSelect, type Choice } from '../ui/AnalysisControls';
import { EmptyState } from '../ui/EmptyState';
import { KpiCard } from '../ui/KpiCard';
import { NumberField } from '../ui/forms';
import { LoadError } from '../ui/LoadError';
import { PageHeader } from '../ui/PageHeader';
import { Skeleton, SkeletonCard, SkeletonHeader, SkeletonKpi, SkeletonPage } from '../ui/Skeleton';
import {
  DEFAULTS,
  LIMITS,
  calculate,
  parseField,
  pickDefaultCycle,
  pickDefaultWork,
  readDraft,
  windowDays,
  writeDraft,
  type Assumptions,
  type NumericField,
} from './planningCalc';
import './planning.css';

const m = defineMessages({
  trailLevel: { id: 'station.planning.trail.level', defaultMessage: 'Район', description: 'Хлебная крошка: уровень просмотра «Район»' },
  trailName: { id: 'station.planning.trail.name', defaultMessage: 'Планирование ресурсов', description: 'Хлебная крошка: страница «Планирование ресурсов»' },
  title: { id: 'station.planning.title', defaultMessage: 'Планирование ресурсов', description: 'Заголовок страницы планирования ресурсов под работу плана агроцикла' },
  purpose: {
    id: 'station.planning.purpose',
    defaultMessage: 'Хватит ли техники, воды и денег на площадь в окне работы плана. Площадь, окно и воду берём из системы; технику, нормы и бюджет задаёте вы.',
    description: 'Подзаголовок страницы планирования (одна строка под заголовком): что считает калькулятор и что откуда берётся',
  },
  loadingLabel: { id: 'station.planning.loading', defaultMessage: 'планирование ресурсов', description: 'Что загружается: подпись заглушки страницы планирования для чтеца с экрана; строчными' },
  controlCycle: { id: 'station.planning.control.cycle', defaultMessage: 'Агроцикл', description: 'Подпись списка выбора агроцикла в шапке страницы планирования; коротко' },
  controlWork: { id: 'station.planning.control.work', defaultMessage: 'Работа плана', description: 'Подпись списка выбора работы плана агроцикла в шапке страницы планирования; коротко' },
  statusStarted: { id: 'station.planning.status.started', defaultMessage: 'идёт', description: 'Пометка в списке агроциклов: агроцикл начат; строчными, коротко' },
  statusNotStarted: { id: 'station.planning.status.notStarted', defaultMessage: 'не начат', description: 'Пометка в списке агроциклов: агроцикл ещё не начат (будущий сезон); строчными, коротко' },
  statusFinished: { id: 'station.planning.status.finished', defaultMessage: 'завершён', description: 'Пометка в списке агроциклов: агроцикл завершён; строчными, коротко' },
  workOption: { id: 'station.planning.workOption', defaultMessage: '{stage} · {window}', description: 'Пояснение под названием работы в списке выбора: этап плана и окно работы; {stage} — название этапа, {window} — даты окна («5 окт — 30 окт 2026»)' },
  workOptionNoWindow: { id: 'station.planning.workOptionNoWindow', defaultMessage: '{stage} · без дат', description: 'Пояснение под названием работы в списке выбора, когда окно у работы не задано; {stage} — название этапа' },

  noCyclesTitle: { id: 'station.planning.noCycles.title', defaultMessage: 'Агроциклов нет', description: 'Заголовок пустого состояния страницы планирования: в срезе нет ни одного агроцикла' },
  noCyclesReason: { id: 'station.planning.noCycles.reason', defaultMessage: 'в срезе нет ни одного агроцикла, поэтому планировать не на чем', description: 'Пояснение пустого состояния страницы планирования: нет агроциклов; строчными' },
  noWorksTitle: { id: 'station.planning.noWorks.title', defaultMessage: 'У агроцикла нет работ плана', description: 'Заголовок пустого состояния страницы планирования: в плане выбранного агроцикла нет работ' },
  noWorksReason: { id: 'station.planning.noWorks.reason', defaultMessage: 'в плане агроцикла «{cycle}» нет ни одной работы, поэтому окно взять неоткуда. Работы плана добавляет менеджер на странице агроцикла.', description: 'Пояснение пустого состояния страницы планирования; {cycle} — имя агроцикла («Хлопок-2027»)' },
  openCycle: { id: 'station.planning.openCycle', defaultMessage: 'Открыть агроцикл', description: 'Ссылка: перейти на страницу выбранного агроцикла' },
  noWindowTitle: { id: 'station.planning.noWindow.title', defaultMessage: 'У работы нет окна', description: 'Заголовок пустого состояния: ни у работы, ни у её этапа, ни у плана нет дат окна' },
  noWindowReason: { id: 'station.planning.noWindow.reason', defaultMessage: 'ни у работы, ни у её этапа, ни у плана агроцикла нет дат, поэтому считать время не на чем. Выберите другую работу.', description: 'Пояснение пустого состояния: даты окна не заданы нигде' },
  loadWhatCycles: { id: 'station.planning.loadWhat.cycles', defaultMessage: 'агроциклы', description: 'Что не загрузилось (винительный падеж) в «Не удалось загрузить …»: список агроциклов' },
  loadWhatPlan: { id: 'station.planning.loadWhat.plan', defaultMessage: 'план агроцикла', description: 'Что не загрузилось (винительный падеж) в «Не удалось загрузить …»: план агроцикла' },

  kpiTimeLabel: { id: 'station.planning.kpi.time.label', defaultMessage: 'Нужно времени', description: 'Подпись KPI: сколько дней нужно на всю площадь заданными агрегатами; коротко' },
  kpiTimeReserve: { id: 'station.planning.kpi.time.reserve', defaultMessage: 'окно {window} дн. · запас {days} дн.', description: 'Нижняя строка KPI «Нужно времени», когда окна хватает; {window} — дней в окне, {days} — запас в днях' },
  kpiTimeGap: { id: 'station.planning.kpi.time.gap', defaultMessage: 'окно {window} дн. · не хватает {days} дн.', description: 'Нижняя строка KPI «Нужно времени», когда окна не хватает; {window} — дней в окне, {days} — на сколько дней больше окна' },
  kpiUnitsLabel: { id: 'station.planning.kpi.units.label', defaultMessage: 'Нужно техники', description: 'Подпись KPI: сколько агрегатов нужно, чтобы уложиться в окно; коротко' },
  kpiUnitsUnit: { id: 'station.planning.kpi.units.unit', defaultMessage: '{count, plural, one {агрегат} few {агрегата} many {агрегатов} other {агрегата}}', description: 'Единица рядом с числом в KPI «Нужно техники»; без числа, склоняется по {count}' },
  kpiUnitsOk: { id: 'station.planning.kpi.units.ok', defaultMessage: 'заложено {units} ед. · хватает', description: 'Нижняя строка KPI «Нужно техники», когда агрегатов хватает; {units} — сколько задано в допущениях' },
  kpiUnitsShort: { id: 'station.planning.kpi.units.short', defaultMessage: 'заложено {units} ед. · не хватает {short} ед.', description: 'Нижняя строка KPI «Нужно техники», когда агрегатов не хватает; {units} — задано, {short} — сколько не хватает' },
  kpiWaterLabel: { id: 'station.planning.kpi.water.label', defaultMessage: 'Нужно воды', description: 'Подпись KPI: сколько воды нужно на площадь работы; коротко' },
  kpiWaterFile: { id: 'station.planning.kpi.water.file', defaultMessage: 'подано по файлу ирригации: {delivered}, на {date}', description: 'Нижняя строка KPI «Нужно воды»: сколько воды подано за агроцикл по файлу ирригации; {delivered} — объём с единицей, {date} — дата, по которую есть данные файла' },
  kpiWaterNoFile: { id: 'station.planning.kpi.water.noFile', defaultMessage: 'подача по файлу ирригации не измерена', description: 'Нижняя строка KPI «Нужно воды»: файла ирригации по агроциклу нет; честное «нет данных», а не 0' },
  kpiWaterNotIrrigation: { id: 'station.planning.kpi.water.notIrrigation', defaultMessage: 'работа не поливная — воды не требует', description: 'Нижняя строка KPI «Нужно воды», когда выбрана не поливная работа' },
  kpiWaterFailed: { id: 'station.planning.kpi.water.failed', defaultMessage: 'подача по файлу не загрузилась', description: 'Нижняя строка KPI «Нужно воды», когда запрос подачи воды упал' },
  kpiBudgetLabel: { id: 'station.planning.kpi.budget.label', defaultMessage: 'Нужно денег', description: 'Подпись KPI: сколько денег нужно на площадь работы; коротко' },
  kpiBudgetValue: { id: 'station.planning.kpi.budget.value', defaultMessage: '{value} млн', description: 'Значение KPI «Нужно денег»: миллионы сумов числом; {value} — число' },
  kpiBudgetUnit: { id: 'station.planning.kpi.budget.unit', defaultMessage: 'сум', description: 'Единица рядом со значением KPI «Нужно денег»; коротко' },
  kpiBudgetOk: { id: 'station.planning.kpi.budget.ok', defaultMessage: 'заложено {have} млн · запас {reserve} млн', description: 'Нижняя строка KPI «Нужно денег», когда бюджета хватает; {have} — бюджет, {reserve} — запас, миллионы сумов числами' },
  kpiBudgetShort: { id: 'station.planning.kpi.budget.short', defaultMessage: 'заложено {have} млн · не хватает {gap} млн', description: 'Нижняя строка KPI «Нужно денег», когда бюджета не хватает; {have} — бюджет, {gap} — нехватка, миллионы сумов числами' },
  noAreaTitle: { id: 'station.planning.noArea.title', defaultMessage: 'Нет площади для расчёта', description: 'Заголовок пустого состояния блока результатов: у агроцикла нет земли' },
  noAreaReason: { id: 'station.planning.noArea.reason', defaultMessage: 'в агроцикле нет размещённых полей (будущий сезон или размещение ещё не внесено). Введите площадь работы в допущениях слева.', description: 'Пояснение пустого состояния блока результатов: земли нет, площадь вводится вручную' },

  assumptionsTitle: { id: 'station.planning.assumptions.title', defaultMessage: 'Допущения', description: 'Заголовок карточки с полями ввода: техника, нормы, бюджет, которые задаёт пользователь' },
  draftBadge: { id: 'station.planning.draft', defaultMessage: 'черновик', description: 'Метка у карточки допущений: значения хранятся только в этом браузере и не являются данными района; строчными, коротко' },
  draftNote: { id: 'station.planning.draft.note', defaultMessage: 'Допущения хранятся только в этом браузере и на сервер не уходят. Техники и бюджета в базе района нет, поэтому их задаёте вы.', description: 'Пояснение под заголовком карточки допущений' },
  fieldArea: { id: 'station.planning.field.workArea', defaultMessage: 'Площадь работы', description: 'Подпись числового поля: сколько гектаров обрабатывается в окне' },
  fieldUnits: { id: 'station.planning.field.units', defaultMessage: 'Доступные агрегаты', description: 'Подпись числового поля: сколько единиц техники (агрегатов) доступно' },
  fieldRate: { id: 'station.planning.field.rate', defaultMessage: 'Производительность одного', description: 'Подпись числового поля: сколько гектаров в день обрабатывает один агрегат' },
  fieldWaterPerHa: { id: 'station.planning.field.waterNorm', defaultMessage: 'Норма воды', description: 'Подпись числового поля: норма воды на гектар для поливной работы' },
  fieldCostPerHa: { id: 'station.planning.field.costPerHa', defaultMessage: 'Расходы на выбранные работы', description: 'Подпись числового поля: затраты на гектар выбранных работ' },
  fieldBudget: { id: 'station.planning.field.budget', defaultMessage: 'Доступный бюджет', description: 'Подпись числового поля: сколько денег доступно' },
  unitDays: { id: 'station.planning.unit.days', defaultMessage: 'дн.', description: 'Единица справа от поля: дни; коротко' },
  unitHa: { id: 'station.planning.unit.ha', defaultMessage: 'га', description: 'Единица справа от поля: гектары; коротко' },
  unitUnits: { id: 'station.planning.unit.units', defaultMessage: 'ед.', description: 'Единица справа от поля: единицы техники; коротко' },
  unitHaDay: { id: 'station.planning.unit.haDay', defaultMessage: 'га/день', description: 'Единица справа от поля: гектаров в день; коротко' },
  unitM3Ha: { id: 'station.planning.unit.m3Ha', defaultMessage: 'м³/га', description: 'Единица справа от поля: кубометров воды на гектар; коротко' },
  unitThousandSumHa: { id: 'station.planning.unit.thousandSumHa', defaultMessage: 'тыс. сум/га', description: 'Единица справа от поля: тысячи сумов на гектар; коротко' },
  unitMlnSum: { id: 'station.planning.unit.mlnSum', defaultMessage: 'млн сум', description: 'Единица справа от поля: миллионы сумов; коротко' },
  areaHintCanon: { id: 'station.planning.area.hintCanon', defaultMessage: 'из агроцикла: {area}', description: 'Подсказка под полем «Площадь работы»: площадь взята из земли агроцикла; {area} — гектары с единицей' },
  areaHintManual: { id: 'station.planning.area.hintManual', defaultMessage: 'введена вручную · в агроцикле {area}', description: 'Подсказка под полем «Площадь работы»: площадь введена вручную; {area} — земля агроцикла с единицей' },
  areaHintNone: { id: 'station.planning.area.hintNone', defaultMessage: 'в агроцикле нет размещённых полей — введите площадь', description: 'Подсказка под полем «Площадь работы»: в агроцикле земли нет, площадь вводится вручную' },
  areaReset: { id: 'station.planning.area.reset', defaultMessage: 'взять из агроцикла', description: 'Ссылка-кнопка рядом с подсказкой поля «Площадь работы»: вернуть площадь агроцикла; строчными' },
  waterHintOff: { id: 'station.planning.water.hintOff', defaultMessage: 'нужна только для поливных работ', description: 'Подсказка под полем «Норма воды», когда выбрана не поливная работа; строчными' },
  reset: { id: 'station.planning.reset', defaultMessage: 'Сбросить допущения', description: 'Кнопка: вернуть допущения (техника, нормы, бюджет) к значениям по умолчанию' },
  storageFailed: { id: 'station.planning.storageFailed', defaultMessage: 'Браузер не разрешил сохранить допущения. Расчёт на экране остаётся.', description: 'Сообщение-ошибка под полями допущений, когда браузер не дал записать их' },

  sourceTitle: { id: 'station.planning.source.title', defaultMessage: 'Что берётся из системы', description: 'Заголовок карточки: данные, которые страница читает из базы района (агроцикл, площадь, окно, вода)' },
  sourceCycle: { id: 'station.planning.source.cycle', defaultMessage: 'Агроцикл', description: 'Строка карточки «Что берётся из системы»: название агроцикла' },
  sourceArea: { id: 'station.planning.source.area', defaultMessage: 'Земля агроцикла', description: 'Строка карточки «Что берётся из системы»: земля агроцикла в гектарах' },
  sourceAreaNone: { id: 'station.planning.source.areaNone', defaultMessage: 'не измерена', description: 'Значение строки «Земля агроцикла», когда размещения нет; честное «нет данных», а не 0' },
  sourceAreaFacts: { id: 'station.planning.source.areaFacts', defaultMessage: '{farms} · {fields}', description: 'Мелкая строка под землёй агроцикла; {farms} — «337 хозяйств», {fields} — «2 480 полей»' },
  sourceFarms: { id: 'station.planning.source.farms', defaultMessage: '{count, plural, one {# хозяйство} few {# хозяйства} many {# хозяйств} other {# хозяйства}}', description: 'Число хозяйств в агроцикле' },
  sourceFields: { id: 'station.planning.source.fields', defaultMessage: '{count, plural, one {# поле} few {# поля} many {# полей} other {# поля}}', description: 'Число полей в агроцикле' },
  sourceWork: { id: 'station.planning.source.work', defaultMessage: 'Работа плана', description: 'Строка карточки «Что берётся из системы»: выбранная работа плана' },
  sourceWindow: { id: 'station.planning.source.window', defaultMessage: 'Окно', description: 'Строка карточки «Что берётся из системы»: даты рабочего окна' },
  sourceWindowDays: { id: 'station.planning.source.windowDays', defaultMessage: '{days} дн.', description: 'Мелкая строка под окном: сколько календарных дней в окне; {days} — число' },
  sourceWindowStage: { id: 'station.planning.source.windowStage', defaultMessage: 'у работы дат нет — окно этапа «{stage}»', description: 'Мелкая строка под окном: окно взято у этапа; {stage} — название этапа' },
  sourceWindowPlan: { id: 'station.planning.source.windowPlan', defaultMessage: 'у работы и этапа дат нет — окно всего плана', description: 'Мелкая строка под окном: окно взято у всего плана агроцикла' },
  sourceWater: { id: 'station.planning.source.water', defaultMessage: 'Вода по файлу', description: 'Строка карточки «Что берётся из системы»: сколько воды подано по файлу ирригации' },
  sourceWaterNone: { id: 'station.planning.source.waterNone', defaultMessage: 'не измерена', description: 'Значение строки «Вода по файлу», когда файла ирригации по агроциклу нет; честное «нет данных», а не 0' },
  sourceWaterFacts: { id: 'station.planning.source.waterFacts', defaultMessage: 'полито {land} · на {date}', description: 'Мелкая строка под водой по файлу; {land} — политая земля с единицей, {date} — дата, по которую есть данные' },
  openWater: { id: 'station.planning.openWater', defaultMessage: 'Вода агроцикла', description: 'Ссылка: перейти на экран «Вода» выбранного агроцикла' },
  trackLabel: { id: 'station.planning.track.label', defaultMessage: 'Нужно {need} дн. из {window} дн. окна', description: 'Подпись шкалы под окном: сколько дней нужно из дней окна; для чтеца с экрана; {need}, {window} — числа' },
  footnote: {
    id: 'station.planning.footnote',
    defaultMessage: '{units, plural, one {# агрегат} few {# агрегата} many {# агрегатов} other {# агрегата}} × {rate} га/день = {daily} га/день. Дни подряд, без поправки на погоду, простои и выходные. Гектары × заданная норма или затраты на гектар; дефицит ресурса не превращается в оценку потерь урожая.',
    description: 'Сноска под расчётом: формула и оговорки; {units} — число агрегатов, {rate} и {daily} — гектаров в день (числа)',
  },
});

/** Работа плана с окном, которое реально используется в расчёте. */
interface PlanWork {
  id: number;
  label: string;
  stageCode: string;
  stageName: string;
  category: string;
  from: string | null;
  to: string | null;
  /** Откуда окно: у самой работы, у её этапа или у всего плана. */
  source: 'work' | 'stage' | 'plan';
  upcoming: string | null;
  campaignStatus: string | null;
}

function flattenPlan(detail: AgrocycleDetail): PlanWork[] {
  const out: PlanWork[] = [];
  for (const stage of detail.plan.stages) {
    for (const w of stage.works) {
      const own = w.window_from && w.window_to;
      const byStage = stage.window_from && stage.window_to;
      out.push({
        id: w.id,
        label: w.label,
        stageCode: stage.code,
        stageName: stage.name,
        category: w.category,
        from: own ? w.window_from! : byStage ? stage.window_from! : (detail.plan.plan_from ?? null),
        to: own ? w.window_to! : byStage ? stage.window_to! : (detail.plan.plan_to ?? null),
        source: own ? 'work' : byStage ? 'stage' : 'plan',
        upcoming: w.upcoming ?? null,
        campaignStatus: w.campaign_status ?? null,
      });
    }
  }
  return out;
}

interface NumFieldProps {
  label: string;
  unit: string;
  /** Допущение, по чьим границам принимается ввод (`LIMITS`). */
  field: NumericField;
  /** Значение в единицах хранения; `null` — поле пустое (число не названо). */
  value: number | null;
  /** Во сколько раз единица на экране крупнее единицы хранения (тыс. сум, млн сум). */
  scale?: number;
  onCommit: (value: number) => void;
  hint?: ReactNode;
  disabled?: boolean;
}

/**
 * Числовое допущение: `NumberField` «Основы форм» с единицей внутри поля.
 * Значение принимается при уходе из поля или по Enter и зажимается в границы
 * `LIMITS`; пустое поле возвращает последнее принятое значение. Шаг не задаём:
 * с `step` поле подгоняет значение к сетке от `minValue` (17 012,5 га станет
 * 17 001), а допущение должно показываться ровно таким, какое принято.
 */
function NumField({ label, unit, field, value, scale = 1, onCommit, hint, disabled }: NumFieldProps) {
  const { min, max, integer } = LIMITS[field];
  return (
    <NumberField
      className="plan-field"
      label={label}
      unit={unit}
      value={value === null ? null : value / scale}
      minValue={min / scale}
      maxValue={max / scale}
      fractionDigits={integer ? 0 : scale > 1 ? 3 : 2}
      description={hint}
      isDisabled={disabled}
      onChange={(next) => {
        if (next === null) return;
        const stored = parseField(field, String(scale === 1 ? next : Math.round(next * scale)));
        if (stored !== null) onCommit(stored);
      }}
    />
  );
}

/** Скелет страницы: настоящий заголовок и размеры готовой раскладки. */
function PlanningSkeleton({ title, label }: { title: string; label: string }) {
  return (
    <SkeletonPage label={label} className="plan">
      <SkeletonHeader title={title} controls={2} />
      <div className="plan-kpis">{Array.from({ length: 4 }, (_, i) => <SkeletonKpi key={i} quiet />)}</div>
      <div className="plan-cols">
        <SkeletonCard quiet height={360}><Skeleton variant="block" width="100%" height={300} /></SkeletonCard>
        <SkeletonCard quiet height={360}><Skeleton variant="block" width="100%" height={300} /></SkeletonCard>
      </div>
    </SkeletonPage>
  );
}

export function Planning() {
  const t = useT();
  const ctx = useAnalysisContext();
  const [search, setSearch] = useSearchParams();
  usePageTrail([{ level: t(m.trailLevel), name: t(m.trailName) }]);

  /* --- канон: агроциклы, план выбранного, вода ------------------------- */
  const cycles = useRequest(() => agrocycleApi.list(ctx.params), [ctx.key]);
  const rows: AgrocycleRow[] = useMemo(() => cycles.data?.items ?? [], [cycles.data]);
  const cycleParam = search.get('cycle');
  const cycleKey = cycleParam && rows.some((r) => r.key === cycleParam) ? cycleParam : pickDefaultCycle(rows);
  const cycleRow = rows.find((r) => r.key === cycleKey) ?? null;

  const detailReq = useRequest<AgrocycleDetail | null>(
    () => (cycleKey ? agrocycleApi.detail(cycleKey, ctx.params) : Promise.resolve(null)),
    [ctx.key, cycleKey],
  );
  // Прежний результат остаётся на экране, пока грузится следующий: чужой цикл за свой не выдаём.
  const detail = detailReq.data && detailReq.data.key === cycleKey ? detailReq.data : null;
  const waterReq = useRequest(
    () => (cycleKey ? api.waterDistrict({ ...ctx.params, cycle: cycleKey }) : Promise.resolve(null)),
    [ctx.key, cycleKey],
  );

  const works = useMemo(() => (detail ? flattenPlan(detail) : []), [detail]);
  const today = ctx.asOf ?? cycles.data?.horizon ?? localIsoDate();
  const workParam = search.get('work');
  const workId = workParam && works.some((w) => String(w.id) === workParam)
    ? Number(workParam)
    : pickDefaultWork(works.map((w) => ({ id: w.id, window_from: w.from, window_to: w.to, upcoming: w.upcoming, campaign_status: w.campaignStatus })), today);
  const work = works.find((w) => w.id === workId) ?? null;

  const setParam = (key: string, value: string) => setSearch((prev) => {
    const next = new URLSearchParams(prev);
    next.set(key, value);
    if (key === 'cycle') next.delete('work');
    return next;
  }, { replace: true });

  /* --- допущения: черновик в браузере ---------------------------------- */
  const [draft, setDraft] = useState<Assumptions>(readDraft);
  const [storageOk, setStorageOk] = useState(true);
  const update = (patch: Partial<Assumptions>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    setStorageOk(writeDraft(next));
  };

  /* --- шапка: списки выбора -------------------------------------------- */
  const statusNote = (status: string) => (status === 'started' ? t(m.statusStarted) : status === 'finished' ? t(m.statusFinished) : t(m.statusNotStarted));
  const cycleOptions: Choice[] = rows.map((r) => ({ id: r.key, name: cycleLabel(r), description: statusNote(r.status) }));
  const workOptions: Choice[] = works.map((w) => ({
    id: String(w.id),
    name: srv(w.label),
    description: w.from && w.to ? t(m.workOption, { stage: srv(w.stageName), window: windowLabel(w.from, w.to) }) : t(m.workOptionNoWindow, { stage: srv(w.stageName) }),
  }));
  const title = t(m.title);

  /* --- состояния -------------------------------------------------------- */
  if (cycles.state === 'error' && !cycles.data) {
    return <div className="plan"><PageHeader level="district" title={title} purpose={t(m.purpose)} /><LoadError what={t(m.loadWhatCycles)} error={cycles.error} onRetry={cycles.reload} /></div>;
  }
  if (!cycles.data) return <PlanningSkeleton title={title} label={t(m.loadingLabel)} />;
  if (!rows.length || !cycleRow || !cycleKey) {
    return <div className="plan"><PageHeader level="district" title={title} purpose={t(m.purpose)} /><Card><EmptyState title={t(m.noCyclesTitle)} reason={t(m.noCyclesReason)} /></Card></div>;
  }

  const cycleName = cycleLabel(cycleRow);
  const header = (
    <PageHeader
      level="district"
      title={title}
      purpose={t(m.purpose)}
      actions={(
        <div className="plan-controls">
          <ChoiceSelect label={t(m.controlCycle)} value={cycleKey} options={cycleOptions} onChange={(v) => setParam('cycle', v)} />
          {works.length ? <ChoiceSelect label={t(m.controlWork)} value={work ? String(work.id) : ''} options={workOptions} onChange={(v) => setParam('work', v)} /> : null}
        </div>
      )}
    />
  );
  const cycleHref = `/cycles/${encodeURIComponent(cycleKey)}${ctx.linkSearch()}`;

  if (detailReq.state === 'error' && !detail) {
    return <div className="plan">{header}<LoadError what={t(m.loadWhatPlan)} error={detailReq.error} onRetry={detailReq.reload} /></div>;
  }
  if (!detail) {
    return <PlanningSkeleton title={title} label={t(m.loadingLabel)} />;
  }
  if (!work) {
    return (
      <div className="plan">
        {header}
        <Card><EmptyState title={t(m.noWorksTitle)} reason={t(m.noWorksReason, { cycle: cycleName })} action={<Link className="astra-button" to={cycleHref}>{t(m.openCycle)} <ArrowUpRight size={15} /></Link>} /></Card>
      </div>
    );
  }
  if (!work.from || !work.to) {
    return <div className="plan">{header}<Card><EmptyState title={t(m.noWindowTitle)} reason={t(m.noWindowReason)} /></Card></div>;
  }

  /* --- расчёт ------------------------------------------------------------ */
  const canonArea = detail.participants.land_ha;
  const manualArea = draft.areaByCycle[cycleKey];
  const area = manualArea ?? canonArea;
  const days = windowDays(work.from, work.to);
  const irrigation = work.category === 'irrigation';
  const result = area !== null && area > 0 && days !== null
    ? calculate({ areaHa: area, windowDays: days, units: draft.units, haPerUnitDay: draft.haPerUnitDay, waterPerHa: draft.waterPerHa, costPerHa: draft.costPerHa, budget: draft.budget, irrigation })
    : null;

  const metric = (id: string) => waterReq.data?.metrics.find((x) => x.id === id)?.value ?? null;
  const delivered = metric('water.delivered_m3');
  const irrigatedHa = metric('water.irrigated_land_ha');
  const waterThrough = waterReq.data?.freshness.data_through ?? null;
  const mln = (v: number) => num(Math.round(v / 1e6));

  const setArea = (v: number) => update({ areaByCycle: { ...draft.areaByCycle, [cycleKey]: v } });
  const resetArea = () => {
    const rest = { ...draft.areaByCycle };
    delete rest[cycleKey];
    update({ areaByCycle: rest });
  };
  const areaHint = manualArea !== undefined
    ? (
      <>
        {canonArea !== null ? t(m.areaHintManual, { area: haUnit(canonArea) }) : t(m.areaHintNone)}
        {canonArea !== null ? <> · <button type="button" className="astra-link" onClick={resetArea}>{t(m.areaReset)}</button></> : null}
      </>
    )
    : canonArea !== null ? t(m.areaHintCanon, { area: haUnit(canonArea) }) : t(m.areaHintNone);

  const waterFailed = waterReq.state === 'error' && !waterReq.data;
  const waterNote = !irrigation
    ? t(m.kpiWaterNotIrrigation)
    : waterFailed
      ? t(m.kpiWaterFailed)
      : delivered === null
        ? t(m.kpiWaterNoFile)
        : t(m.kpiWaterFile, { delivered: m3Compact(delivered), date: waterThrough ? dateDay(waterThrough) : '—' });

  return (
    <div className="plan" data-agent-scope={`${cycleName} · ${srv(work.label)}`}>
      {header}

      {result && days !== null ? (
        <div className="plan-kpis" data-guide="plan-window">
          <KpiCard
            icon={CalendarClock}
            label={t(m.kpiTimeLabel)}
            value={num(result.requiredDays)}
            unit={t(m.unitDays)}
            tone={result.reserveDays < 0 ? 'warning' : undefined}
            note={result.reserveDays < 0 ? t(m.kpiTimeGap, { window: num(days), days: num(-result.reserveDays) }) : t(m.kpiTimeReserve, { window: num(days), days: num(result.reserveDays) })}
          />
          <KpiCard
            icon={Tractor}
            label={t(m.kpiUnitsLabel)}
            value={num(result.requiredUnits)}
            unit={t(m.kpiUnitsUnit, { count: result.requiredUnits })}
            tone={result.unitsShort > 0 ? 'warning' : undefined}
            note={result.unitsShort > 0 ? t(m.kpiUnitsShort, { units: num(draft.units), short: num(result.unitsShort) }) : t(m.kpiUnitsOk, { units: num(draft.units) })}
          />
          <KpiCard
            icon={Droplets}
            label={t(m.kpiWaterLabel)}
            value={m3Compact(result.waterM3 ?? 0)}
            note={waterNote}
          />
          <KpiCard
            icon={Wallet}
            label={t(m.kpiBudgetLabel)}
            value={t(m.kpiBudgetValue, { value: mln(result.costSum) })}
            unit={t(m.kpiBudgetUnit)}
            tone={result.budgetShort > 0 ? 'warning' : undefined}
            note={result.budgetShort > 0 ? t(m.kpiBudgetShort, { have: mln(draft.budget), gap: mln(result.budgetShort) }) : t(m.kpiBudgetOk, { have: mln(draft.budget), reserve: mln(result.budgetReserve) })}
          />
        </div>
      ) : (
        <div data-guide="plan-window"><Card><EmptyState compact title={t(m.noAreaTitle)} reason={t(m.noAreaReason)} /></Card></div>
      )}

      <div className="plan-cols">
        <div data-guide="plan-conditions">
          <Card title={t(m.assumptionsTitle)} aside={<Badge tone="copper">{t(m.draftBadge)}</Badge>}>
            <p className="plan-note">{t(m.draftNote)}</p>
            <div className="plan-form">
              <NumField
                label={t(m.fieldArea)}
                unit={t(m.unitHa)}
                field="area"
                value={area}
                onCommit={setArea}
               
                hint={areaHint}
              />
              <NumField label={t(m.fieldUnits)} unit={t(m.unitUnits)} field="units" value={draft.units} onCommit={(v) => update({ units: v })} />
              <NumField label={t(m.fieldRate)} unit={t(m.unitHaDay)} field="haPerUnitDay" value={draft.haPerUnitDay} onCommit={(v) => update({ haPerUnitDay: v })} />
              <NumField label={t(m.fieldWaterPerHa)} unit={t(m.unitM3Ha)} field="waterPerHa" value={draft.waterPerHa} onCommit={(v) => update({ waterPerHa: v })} disabled={!irrigation} hint={!irrigation ? t(m.waterHintOff) : undefined} />
              <NumField label={t(m.fieldCostPerHa)} unit={t(m.unitThousandSumHa)} field="costPerHa" scale={1e3} value={draft.costPerHa} onCommit={(v) => update({ costPerHa: v })} />
              <NumField label={t(m.fieldBudget)} unit={t(m.unitMlnSum)} field="budget" scale={1e6} value={draft.budget} onCommit={(v) => update({ budget: v })} />
            </div>
            {!storageOk ? <p className="plan-warn" role="status">{t(m.storageFailed)}</p> : null}
            <div className="plan-actions">
              <button type="button" className="astra-button" onClick={() => { setDraft({ ...DEFAULTS, areaByCycle: {} }); setStorageOk(writeDraft({ ...DEFAULTS, areaByCycle: {} })); }}>
                <RotateCcw size={15} /> {t(m.reset)}
              </button>
            </div>
          </Card>
        </div>

        <div data-guide="plan-balance">
          <Card title={t(m.sourceTitle)}>
            <dl className="plan-facts">
              <div><dt>{t(m.sourceCycle)}</dt><dd><Link to={cycleHref}>{cycleName}</Link></dd></div>
              <div>
                <dt>{t(m.sourceArea)}</dt>
                <dd>
                  {canonArea !== null ? haUnit(canonArea) : t(m.sourceAreaNone)}
                  <small>{t(m.sourceAreaFacts, { farms: t(m.sourceFarms, { count: detail.participants.farms }), fields: t(m.sourceFields, { count: detail.participants.fields }) })}</small>
                </dd>
              </div>
              <div>
                <dt>{t(m.sourceWork)}</dt>
                <dd>{srv(work.label)}<small>{srv(work.stageName)}</small></dd>
              </div>
              <div>
                <dt>{t(m.sourceWindow)}</dt>
                <dd>
                  {windowLabel(work.from, work.to)}
                  <small>
                    {work.source === 'work' ? t(m.sourceWindowDays, { days: num(days) }) : work.source === 'stage' ? t(m.sourceWindowStage, { stage: srv(work.stageName) }) : t(m.sourceWindowPlan)}
                  </small>
                </dd>
              </div>
              <div>
                <dt>{t(m.sourceWater)}</dt>
                <dd>
                  {delivered === null ? t(m.sourceWaterNone) : m3Compact(delivered)}
                  {delivered !== null ? <small>{t(m.sourceWaterFacts, { land: haUnit(irrigatedHa), date: waterThrough ? dateDay(waterThrough) : '—' })}</small> : null}
                </dd>
              </div>
            </dl>
            {result && days !== null ? (
              <div className="plan-track" role="img" aria-label={t(m.trackLabel, { need: num(result.requiredDays), window: num(days) })}>
                <i className={result.reserveDays < 0 ? 'is-over' : ''} style={{ width: `${Math.min(100, (result.requiredDays / days) * 100)}%` }} />
              </div>
            ) : null}
            {result ? <p className="plan-note">{t(m.footnote, { units: draft.units, rate: num(draft.haPerUnitDay), daily: num(result.dailyHa) })}</p> : null}
            <div className="plan-actions">
              <Link className="astra-button" to={cycleHref}>{t(m.openCycle)} <ArrowUpRight size={15} /></Link>
              <Link className="astra-button" to={`/water${ctx.linkSearch({ cycle: cycleKey })}`}>{t(m.openWater)} <ArrowUpRight size={15} /></Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
